import { CameraOutlined, DeleteOutlined, PictureOutlined } from '@ant-design/icons';
import { Button, Form, Input, Modal, Select, Switch, Upload } from 'antd';
import type { UploadChangeParam } from 'antd/es/upload';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { PreviewImage } from '@/components/PreviewImage/PreviewImage';
import { BRAND } from '@/constants/brand';
import { useNotify } from '@/hooks/useNotify';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useMutationReset } from '@/hooks/useMutationReset';
import { useLazyGetCategoriesQuery } from '@/modules/categories/categoriesApi';
import {
  useCreateSubcategoryMutation,
  useUpdateSubcategoryMutation,
  useUploadSubcategoryImageMutation,
} from '@/modules/subcategories/subcategoriesApi';
import type { Subcategory } from '@/types/subcategory';
import { resolveMutationResult } from '@/utils/apiResponse';
import { getSubcategoryImageUrl } from '@/utils/media';
import { ucfirst } from '@/utils/string';
import styles from '../../categories/components/CategoryFormModal.module.scss';

interface SubcategoryFormModalProps {
  open: boolean;
  subcategory?: Subcategory | null;
  defaultCategoryId?: string;
  onClose: () => void;
}

interface SubcategoryFormValues {
  categoryId: string;
  name: string;
  isActive: boolean;
}

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const MAX_SIZE_BYTES = 5 * 1024 * 1024;

const DROPDOWN_LIST_PARAMS = {
  page: 1,
  limit: 100,
  sortBy: 'name',
  sortOrder: 'asc' as const,
  status: 'active' as const,
};

function buildCategoryOptions(
  items: Array<{ id: string; name: string }>,
  selectedId?: string | null,
  selectedName?: string | null,
) {
  const options = items.map((category) => ({
    label: ucfirst(category.name),
    value: category.id,
  }));

  if (selectedId && selectedName && !options.some((option) => option.value === selectedId)) {
    options.unshift({ label: ucfirst(selectedName), value: selectedId });
  }

  return options;
}

export function SubcategoryFormModal({
  open,
  subcategory,
  defaultCategoryId,
  onClose,
}: SubcategoryFormModalProps) {
  const notify = useNotify();
  const isEdit = Boolean(subcategory);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>();
  const [fetchCategories, { data: categoriesResult, isFetching: categoriesLoading }] =
    useLazyGetCategoriesQuery();
  const [createSubcategory, createState] = useCreateSubcategoryMutation();
  const [updateSubcategory, updateState] = useUpdateSubcategoryMutation();
  const [uploadSubcategoryImage, uploadState] = useUploadSubcategoryImageMutation();
  const isLoading = createState.isLoading || updateState.isLoading || uploadState.isLoading;
  useGlobalLoader(isLoading);

  const categoryOptions = useMemo(
    () =>
      buildCategoryOptions(
        categoriesResult?.results ?? [],
        subcategory?.categoryId ?? defaultCategoryId,
        subcategory?.categoryName,
      ),
    [
      categoriesResult?.results,
      defaultCategoryId,
      subcategory?.categoryId,
      subcategory?.categoryName,
    ],
  );

  const { control, handleSubmit, reset } = useForm<SubcategoryFormValues>({
    defaultValues: {
      categoryId: '',
      name: '',
      isActive: true,
    },
  });

  useMutationReset(createState.reset);
  useMutationReset(updateState.reset);
  useMutationReset(uploadState.reset);

  const existingImageUrl = useMemo(
    () => getSubcategoryImageUrl(subcategory?.image),
    [subcategory?.image],
  );

  useEffect(() => {
    if (!open) return;

    reset({
      categoryId: subcategory?.categoryId ?? defaultCategoryId ?? '',
      name: subcategory?.name ?? '',
      isActive: subcategory?.isActive ?? true,
    });
    setImageFile(null);
    setPreviewUrl(existingImageUrl);

    if (subcategory?.categoryId || defaultCategoryId) {
      void fetchCategories(DROPDOWN_LIST_PARAMS);
    }
  }, [
    defaultCategoryId,
    existingImageUrl,
    fetchCategories,
    open,
    reset,
    subcategory,
  ]);

  const handleImageChange = (info: UploadChangeParam) => {
    const file = info.file.originFileObj ?? info.file;
    if (!file || !(file instanceof File)) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      notify.error('Please upload a PNG, JPG, WEBP, or GIF image.');
      return;
    }

    if (file.size > MAX_SIZE_BYTES) {
      notify.error('Image must be 5MB or smaller.');
      return;
    }

    setImageFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setPreviewUrl(undefined);
  };

  const uploadImageIfNeeded = async (): Promise<string | undefined> => {
    if (!imageFile) {
      return subcategory?.image;
    }

    const formData = new FormData();
    formData.append('image', imageFile);
    const result = await resolveMutationResult<{ image: string }>(uploadSubcategoryImage(formData));

    if (!result.ok) {
      notify.error(result.message);
      return undefined;
    }

    return result.data.image;
  };

  const onSubmit = async (values: SubcategoryFormValues) => {
    const image = await uploadImageIfNeeded();
    if (!image) {
      if (!isEdit) {
        notify.error('Subcategory image is required.');
      }
      return;
    }

    const payload = {
      categoryId: values.categoryId,
      name: values.name.trim(),
      image,
      isActive: values.isActive,
    };

    if (isEdit) {
      if (!subcategory?.id) return;

      const result = await resolveMutationResult(updateSubcategory({ id: subcategory.id, body: payload }));
      if (result.ok) {
        notify.success(result.message ?? `Subcategory ${isEdit ? 'updated' : 'created'} successfully.`);
        onClose();
        return;
      }

      notify.error(result.message);
      return;
    }

    const result = await resolveMutationResult(createSubcategory(payload));

    if (result.ok) {
      notify.success(result.message ?? `Subcategory ${isEdit ? 'updated' : 'created'} successfully.`);
      onClose();
      return;
    }

    notify.error(result.message);
  };

  return (
    <Modal
      title={isEdit ? 'Edit subcategory' : 'Add subcategory'}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnHidden
      width={480}
    >
      <Form layout="vertical" className={styles.form} onFinish={handleSubmit(onSubmit)}>
        <Controller
          name="categoryId"
          control={control}
          rules={{ required: 'Category is required' }}
          render={({ field, fieldState }) => (
            <Form.Item
              label="Category"
              required
              validateStatus={fieldState.error ? 'error' : ''}
              help={fieldState.error?.message}
            >
              <Select
                {...field}
                options={categoryOptions}
                placeholder="Select category"
                showSearch
                loading={categoriesLoading}
                optionFilterProp="label"
                onOpenChange={(visible) => {
                  if (visible) void fetchCategories(DROPDOWN_LIST_PARAMS);
                }}
              />
            </Form.Item>
          )}
        />

        <Controller
          name="name"
          control={control}
          rules={{ required: 'Name is required' }}
          render={({ field, fieldState }) => (
            <Form.Item
              label="Name"
              required
              validateStatus={fieldState.error ? 'error' : ''}
              help={fieldState.error?.message}
            >
              <Input {...field} placeholder="Subcategory name" />
            </Form.Item>
          )}
        />

        <Form.Item label="Image" required={!isEdit} className={styles.imageField}>
          <div className={styles.imagePicker}>
            <Upload
              accept={ACCEPTED_TYPES.join(',')}
              showUploadList={false}
              beforeUpload={() => false}
              onChange={handleImageChange}
              disabled={isLoading}
            >
              <button type="button" className={styles.imageThumb}>
                {previewUrl ? (
                  <PreviewImage
                    src={previewUrl}
                    alt="Subcategory preview"
                    fill
                    className={styles.imagePreview}
                    stopPropagation
                  />
                ) : (
                  <span className={styles.imagePlaceholder}>
                    <PictureOutlined />
                  </span>
                )}
                <span className={styles.imageOverlay}>
                  <CameraOutlined />
                </span>
              </button>
            </Upload>

            <div className={styles.imageMeta}>
              <p className={styles.imageTitle}>
                {previewUrl ? 'Subcategory image selected' : 'Upload subcategory image'}
              </p>
              <p className={styles.imageHint}>PNG, JPG, WEBP or GIF · Max 5MB</p>
              {previewUrl ? (
                <div className={styles.imageActions}>
                  <Upload
                    accept={ACCEPTED_TYPES.join(',')}
                    showUploadList={false}
                    beforeUpload={() => false}
                    onChange={handleImageChange}
                    disabled={isLoading}
                  >
                    <Button size="small" type="default">
                      Change
                    </Button>
                  </Upload>
                  <Button
                    size="small"
                    danger
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={handleRemoveImage}
                    disabled={isLoading}
                  >
                    Remove
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
        </Form.Item>

        <Controller
          name="isActive"
          control={control}
          render={({ field }) => (
            <Form.Item label="Status" className={styles.statusField}>
              <Switch
                checked={field.value}
                onChange={field.onChange}
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                style={{ background: field.value ? BRAND.primary : undefined }}
              />
            </Form.Item>
          )}
        />

        <div className={styles.actions}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            {isEdit ? 'Save changes' : 'Create subcategory'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
